import { Play, Volume2, VolumeX, Maximize2 } from 'lucide-react';
import { useState, useRef } from 'react';

// Real workspace assets by category
const WORKSPACE_ASSETS = {
  'Privacy Booths': {
    image: "https://customer-assets.emergentagent.com/job_97f513fe-a3cb-403b-b669-1693d7f5acab/artifacts/odymdcyd_Large%20Banner%20image%20pf%20Phone%20Booth.png",
    video: "https://customer-assets.emergentagent.com/job_97f513fe-a3cb-403b-b669-1693d7f5acab/artifacts/1a66ojnt_30%20sec%20Loop%20Video%20of%20Phone%20Booth.mp4",
    headline: "Experience Focus Without Distraction",
    description: "See how our Privacy Booths transform open offices into productive environments. Premium acoustic solutions designed for the modern corporate workspace."
  },
  'Modular Office Workstation': {
    image: "https://customer-assets.emergentagent.com/job_97f513fe-a3cb-403b-b669-1693d7f5acab/artifacts/moitgllc_Large%20BAnner%20Image%20of%20Workstations.png",
    video: "https://customer-assets.emergentagent.com/job_97f513fe-a3cb-403b-b669-1693d7f5acab/artifacts/q6aq9hmk_30%20sec%20Loop%20Video%20of%20Workstations.mp4",
    headline: "Designed for Every Workspace",
    description: "See how our modular workstations create efficient, organized office environments. Premium solutions designed for productivity and collaboration."
  }
};

// Supported categories for this section
const SUPPORTED_CATEGORIES = ['Privacy Booths', 'Modular Office Workstation'];

export function RealWorkspaceSection({ category }) {
  const [showVideo, setShowVideo] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const videoRef = useRef(null);
  const containerRef = useRef(null);

  // Only show for supported categories
  if (!SUPPORTED_CATEGORIES.includes(category)) return null;
  
  const assets = WORKSPACE_ASSETS[category];

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  return (
    <section className="my-16 border-t border-b border-border py-12">
      {/* Section Header */}
      <div className="text-center mb-10">
        <p className="text-green-600 uppercase tracking-widest text-sm font-bold mb-3">
          Product in Real Workspace
        </p>
        <h2 className="text-3xl md:text-4xl font-black uppercase mb-4">
          {assets.headline}
        </h2>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          {assets.description}
        </p>
      </div>

      {/* Media Container */}
      <div 
        ref={containerRef}
        className="relative max-w-5xl mx-auto rounded-lg overflow-hidden shadow-2xl border-4 border-green-500"
      >
        {/* Toggle Buttons */}
        <div className="absolute top-4 right-4 z-20 flex gap-2">
          <button
            onClick={() => setShowVideo(false)}
            className={`px-4 py-2 text-sm font-bold uppercase transition-all ${
              !showVideo 
                ? 'bg-green-600 text-white' 
                : 'bg-black/50 text-white hover:bg-black/70'
            }`}
          >
            Photo
          </button>
          <button
            onClick={() => setShowVideo(true)}
            className={`px-4 py-2 text-sm font-bold uppercase transition-all flex items-center gap-2 ${
              showVideo 
                ? 'bg-green-600 text-white' 
                : 'bg-black/50 text-white hover:bg-black/70'
            }`}
          >
            <Play className="w-4 h-4" />
            Video
          </button>
          <button
            onClick={toggleFullscreen}
            className="px-3 py-2 bg-black/50 text-white hover:bg-black/70 transition-all"
            title="Fullscreen"
          >
            <Maximize2 className="w-4 h-4" />
          </button>
        </div>

        {/* Image View */}
        {!showVideo && (
          <div className="relative">
            <img
              src={assets.image}
              alt={`${category} in Modern Office`}
              className="w-full h-auto"
            />
            <div className="absolute bottom-4 left-4">
              <div className="flex items-center gap-3">
                <div className="bg-green-600 text-white px-4 py-2 text-sm font-bold uppercase">
                  Corporate Setting
                </div>
                <div className="bg-white/90 text-black px-4 py-2 text-sm font-bold uppercase">
                  Premium Positioning
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Video View */}
        {showVideo && (
          <div className="relative bg-black">
            <video
              ref={videoRef}
              autoPlay
              muted
              loop
              playsInline
              className="w-full h-auto"
            >
              <source src={assets.video} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
            <div className="absolute bottom-4 left-4">
              <div className="flex items-center gap-3 bg-black/70 px-4 py-2 rounded">
                <VolumeX className="w-4 h-4 text-white" />
                <span className="text-white text-sm">Silent Loop • 30 sec</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Feature Highlights */}
      <div className="grid grid-cols-3 gap-6 mt-10 max-w-4xl mx-auto">
        <div className="text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <span className="text-2xl font-black text-green-700">1:1</span>
          </div>
          <p className="font-bold uppercase text-sm">True Scale</p>
          <p className="text-xs text-muted-foreground mt-1">See actual size in workspace</p>
        </div>
        <div className="text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <span className="text-2xl">🏢</span>
          </div>
          <p className="font-bold uppercase text-sm">Corporate Ready</p>
          <p className="text-xs text-muted-foreground mt-1">Designed for modern offices</p>
        </div>
        <div className="text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <span className="text-2xl">✨</span>
          </div>
          <p className="font-bold uppercase text-sm">Premium Finish</p>
          <p className="text-xs text-muted-foreground mt-1">Executive-grade materials</p>
        </div>
      </div>
    </section>
  );
}

export default RealWorkspaceSection;
